/* WHAT ONE CHAMPIONSHIP IS WORTH TO A REP — pure, zero imports.
 *
 * 🔴 THIS IS NOT THE TAM CHAIN, AND THE DIFFERENCE IS THE WHOLE REASON THE FILE
 * EXISTS. Brian: *"takes the math we discussed on average price per unit, looks
 * at the sport and puts a dollar figure to it."*
 *
 * The TAM chain is `fieldSize × unitsPerBuyer × penetration × price` and it
 * produces the market-sizing figure — $143m at 100% penetration, roughly 11×
 * realised revenue. ⚠️ THAT NUMBER IS USELESS ON A SINGLE RECORD AND A REP WOULD
 * NEVER QUOTE IT. It answers "how big is this market", not "what is this
 * championship worth". Pointing the worklist at it was the specification error
 * Brian corrected.
 *
 * The per-opportunity figure drops TWO of the four factors:
 *
 *   fieldSize    — how many ORGANISATIONS are in the market. Irrelevant: we are
 *                  valuing ONE of them, the one in front of the rep.
 *   penetration  — what share of the market buys. Irrelevant for the same
 *                  reason, and it is the factor pinned at 100% and flagged
 *                  `headlineEligible: false` because a ceiling is not a
 *                  forecast.
 *
 * What is left is the part that is actually measured about THIS kind of event:
 *
 *   ⭐  units for that sport and level  ×  price per unit  =  the record
 *
 * Worked example, from Brian 2026-10-04: HS football measures 60–150 units →
 * **$17,280–$43,200**. (60 × 288 = 17,280; 150 × 288 = 43,200 — arithmetic on
 * his figures, not a measurement of mine.) A number a rep can say out loud to
 * an athletic director, which the ceiling figure never was.
 *
 * ⚠️ `headlineEligible: false` STILL HOLDS — on the CEILING. Nothing here reads
 * it, because nothing here touches `fieldSize` or `penetration`. This is a
 * different, smaller, honest number, and that is the point.
 *
 * ⚠️ IT READS THE SERVED CHAIN AND HARDCODES NO PRICE. $288 is Brian's forward
 * price and the backend serves it as `pricePerUnitUsd`; baking 288 into the
 * client would mean a price change took a deploy and the two would disagree in
 * between. No price on the chain means no figure, with the reason said.
 */

/** How many delivered orders behind a mean before it is a real comparable set. */
export const COMPARABLE_N = 5

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/**
 * Pull the units figure out of the chain, preferring a DISTRIBUTION over a mean.
 *
 * ⚠️ IT READS SEVERAL SHAPES ON PURPOSE. Brian says min/median/max per sport ×
 * level exist; `tamChain.js#readFactors` reads only `{ value, n }`, so either
 * the distribution is in the payload and unread, or it is not served yet. I
 * cannot call `/api/tam/record/:id` from here (Bearer-protected, no SR_JWT), so
 * rather than guess one field name this reads the plausible ones and REPORTS
 * which it found. That is the same discipline `CONFIG_KEY_ALIASES` already uses
 * in the studio, and it is why that `18K Yellow Gold` the frontend had never
 * heard of now renders: the alias list is the seam, and a missing key degrades
 * to the mean instead of to nothing.
 */
export function readUnits(chain) {
  const u = chain?.factors?.unitsPerBuyer
  if (u == null) return null
  const obj = typeof u === 'object' ? u : { value: u }

  const mean = num(obj.value) ?? num(obj.mean)
  const median = num(obj.median) ?? num(obj.p50)
  const low = num(obj.min) ?? num(obj.low) ?? num(obj.p25)
  const high = num(obj.max) ?? num(obj.high) ?? num(obj.p75)
  const n = num(obj.n) ?? num(obj.sampleSize)

  /* The point figure: the MEDIAN when there is one, because a mean on order
   * sizes is dragged by one big conference order and the rep would quote high.
   * A distribution is never collapsed to a mean when the median is available. */
  const point = median ?? mean
  if (point == null && low == null && high == null) return null

  return {
    point,
    low: low ?? null,
    high: high ?? null,
    n,
    /* Which of the two shapes actually answered, stated rather than implied. */
    shape: (low != null && high != null) ? 'distribution' : 'mean-only',
    usedMedian: median != null,
  }
}

/** The forward price per unit, from the chain. Never hardcoded. */
export function readPrice(chain) {
  const p = chain?.factors?.pricePerUnitUsd
  if (p == null) return null
  const v = typeof p === 'object' ? num(p.value) : num(p)
  if (v == null) return null
  return { usd: v, basis: (typeof p === 'object' && p.basis) || 'forward price per unit' }
}

/* The three confidence tiers, strongest first. The LABEL is what a rep reads;
 * `rank` is what orders a tie, so a soft number can never silently outrank a
 * hard one at the same dollar figure. */
export const CONFIDENCE = {
  comparables: { key: 'comparables', rank: 3, label: 'measured from comparable orders' },
  'sport-average': { key: 'sport-average', rank: 2, label: 'sport-level average — thin sample' },
  unmodelled: { key: 'unmodelled', rank: 1, label: 'not modelled yet' },
}

/**
 * THE PER-OPPORTUNITY FIGURE.
 *
 * Returns `{ usd, low, high, units, price, confidence, basis, reason }`.
 *
 * ⚠️ `usd` IS null WHEN THERE IS NO BASIS, AND null IS NEVER ZERO. 2,211 records
 * are unmodelled (Brian, 2026-10-04 — his figure, not one I measured; reconcile
 * it live rather than quoting this line). A zero would say "this championship
 * is worth nothing", which
 * is a claim about the opportunity; null says "we have not measured this sport",
 * which is a claim about our data. Those are different sentences and a rep acts
 * differently on each. The reason travels with the null so the row can say it.
 */
export function opportunityValue(chain) {
  const units = readUnits(chain)
  const price = readPrice(chain)

  if (!units) {
    return {
      usd: null, low: null, high: null, units: null, price,
      confidence: CONFIDENCE.unmodelled,
      basis: '',
      reason: 'No unit history for this sport and level yet, so there is no figure to quote. '
        + 'That is a gap in what we have measured, NOT a championship worth nothing.',
    }
  }
  if (!price) {
    return {
      usd: null, low: null, high: null, units, price: null,
      confidence: CONFIDENCE.unmodelled,
      basis: '',
      reason: 'Units are known for this sport but the chain served no price per unit, so the figure '
        + 'cannot be computed here. Nothing is guessed — the price is the backend\'s to serve.',
    }
  }

  const mul = (n) => (n == null ? null : Math.round(n * price.usd))
  const confidence = units.n != null && units.n >= COMPARABLE_N
    ? CONFIDENCE.comparables
    : CONFIDENCE['sport-average']

  /* ⚠️ THE BASIS NAMES THE SAMPLE, because a mean with no n is an opinion —
   * the same rule `readFactors` already applies to this exact factor. */
  const sample = units.n == null
    ? 'no sample size reported — treat it with suspicion'
    : `from ${units.n.toLocaleString('en-US')} delivered order${units.n === 1 ? '' : 's'}`
  const unitText = units.shape === 'distribution'
    ? `${units.low}–${units.high} units`
    : `${units.point} units${units.usedMedian ? ' (median)' : ' (mean)'}`

  return {
    usd: mul(units.point),
    low: mul(units.low),
    high: mul(units.high),
    units,
    price,
    confidence,
    basis: `${unitText} × $${price.usd} per unit — ${sample}`,
    reason: null,
  }
}

/**
 * THE SORT KEY, AND THE RULE THAT STOPS A NULL VANISHING.
 *
 * ⚠️ Brian: *"a null must not sort as zero and vanish to the bottom forever."*
 * Returning 0 for an unmodelled record would bury every one of them under the
 * cheapest modelled title, permanently, where nobody would ever scroll — which
 * is how a gap in the data becomes invisible instead of getting fixed. (Brian
 * put the unmodelled count in the thousands on 2026-10-04; the rule holds at
 * any size, so no figure is repeated here.)
 *
 * So this returns a BAND, not a number, and the caller groups on it:
 *
 *   `band: 'modelled'`    — has a figure; `usd` orders within the band
 *   `band: 'unmodelled'`  — no figure; its own visible group with a count
 *
 * The unmodelled band is rendered as its own section rather than appended to
 * the end of the modelled one. A section a rep can see and act on is the
 * opposite of a tail they never reach.
 */
export function revenueSortKey(value) {
  if (!value || value.usd == null) {
    return { band: 'unmodelled', usd: null, confidenceRank: CONFIDENCE.unmodelled.rank }
  }
  return {
    band: 'modelled',
    usd: value.usd,
    /* A tie on dollars breaks toward the better-evidenced figure, so a
     * sport-average number never silently outranks a measured one. */
    confidenceRank: value.confidence.rank,
  }
}

/** Compare two values for a revenue-first ordering. Highest dollars first. */
export function byRevenue(a, b) {
  const ka = revenueSortKey(a)
  const kb = revenueSortKey(b)
  if (ka.band !== kb.band) return ka.band === 'modelled' ? -1 : 1
  if (ka.band === 'unmodelled') return 0
  if (kb.usd !== ka.usd) return kb.usd - ka.usd
  return kb.confidenceRank - ka.confidenceRank
}

/** Money, with no decimals — a rep quotes whole dollars. */
export const money = (n) => (n == null ? null : '$' + Math.round(n).toLocaleString('en-US'))

/**
 * The sentence a rep reads on the row. A RANGE when the sample supports one,
 * a single figure when it does not, and the gap stated when there is neither.
 */
export function valueText(value) {
  if (!value || value.usd == null) return null
  if (value.low != null && value.high != null && value.low !== value.high) {
    return `${money(value.low)}–${money(value.high)}`
  }
  return money(value.usd)
}
